import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent, CartUpdateEvent } from '@theme/events';
import { fetchConfig, debounce } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';

/**
 * Component that automatically adds/removes a bonus product at tier 2 threshold
 */
class FreeShippingBonusProduct extends Component {
  // threshold1 is deprecated - free shipping threshold is now calculated dynamically
  // Kept for backwards compatibility but not used in logic
  #threshold1 = 0;
  #threshold2 = 0;
  #bonusProductHandle = '';
  #bonusProductId = /** @type {number | null} */ (null);
  #isProcessing = false;
  #debouncedCheck = debounce(this.#checkAndManageBonusProduct.bind(this), 500);
  #cachedProduct = /** @type {any | null} */ (null);
  #offerVariantChangeHandler = /** @type {((e: Event) => void) | null} */ (null);
  #morphObserver = /** @type {MutationObserver | null} */ (null);
  #shouldShowOffer = false;

  connectedCallback() {
    super.connectedCallback();

    console.log('[FreeShippingBonus] Component connected');

    // Get settings from data attributes (populated from Liquid)
    // threshold1 is deprecated - free shipping threshold is now calculated dynamically in cart-drawer.liquid
    this.#threshold1 = parseInt(this.dataset.threshold1 || '15000', 10);
    this.#threshold2 = parseInt(this.dataset.threshold2 || '30000', 10);
    this.#bonusProductHandle = this.dataset.bonusProductHandle || '';
    this.#bonusProductId = this.dataset.bonusProductId ? parseInt(this.dataset.bonusProductId, 10) : null;

    console.log('[FreeShippingBonus] Configuration:', {
      threshold1: this.#threshold1,
      threshold2: this.#threshold2,
      bonusProductHandle: this.#bonusProductHandle,
      bonusProductId: this.#bonusProductId,
      dataset: {
        threshold1: this.dataset.threshold1,
        threshold2: this.dataset.threshold2,
        bonusProductHandle: this.dataset.bonusProductHandle,
        bonusProductId: this.dataset.bonusProductId,
      },
    });

    if (!this.#bonusProductHandle && !this.#bonusProductId) {
      console.warn('[FreeShippingBonus] No bonus product configured - component disabled');
      return;
    }

    // Listen to cart update events
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    console.log('[FreeShippingBonus] Event listeners attached');

    // Listen to bonus product variant selector changes
    document.addEventListener('change', this.#handleBonusVariantChange);

    // Listen to bonus product add button clicks
    document.addEventListener('click', this.#handleBonusAddButtonClick);

    // Setup MutationObserver to watch for cart items table mutations (from morphing)
    this.#setupMorphObserver();

    // Check on page load - delay slightly to ensure cart drawer is initialized
    console.log('[FreeShippingBonus] Running initial cart check on page load');
    setTimeout(() => {
      this.#checkAndManageBonusProduct();
    }, 200);

    // Also check on page navigation (view transitions)
    if (typeof document.startViewTransition === 'function') {
      document.addEventListener('view-transition-start', () => {
        setTimeout(() => this.#checkAndManageBonusProduct(), 100);
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener('change', this.#handleBonusVariantChange);
    document.removeEventListener('click', this.#handleBonusAddButtonClick);
    
    // Disconnect mutation observer
    if (this.#morphObserver) {
      this.#morphObserver.disconnect();
      this.#morphObserver = null;
    }
  }

  /**
   * Sets up a MutationObserver to watch for cart items table mutations
   * When the table is morphed/replaced, re-add the offer row if needed
   */
  #setupMorphObserver() {
    // Wait for cart items table to exist
    const checkForTable = () => {
      const cartItemsTable = document.querySelector('.cart-items__table tbody');
      if (cartItemsTable && !this.#morphObserver) {
        console.log('[FreeShippingBonus] Setting up MutationObserver on cart items table');
        
        this.#morphObserver = new MutationObserver((mutations) => {
          // Check if the tbody or its children were significantly changed (morphed)
          const hasSignificantChange = mutations.some((mutation) => {
            return mutation.type === 'childList' && 
                   (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0);
          });

          if (hasSignificantChange && this.#shouldShowOffer) {
            console.log('[FreeShippingBonus] Cart items table mutated, checking if offer row needs to be re-added');
            // Wait a tick for morphing to complete
            setTimeout(() => {
              // Check if offer row still exists, if not and we should show it, re-add it
              const existingOfferRow = document.querySelector('.cart-items__bonus-offer-row');
              if (!existingOfferRow && this.#shouldShowOffer) {
                console.log('[FreeShippingBonus] Offer row was removed by morphing, re-adding...');
                // Fetch cart to check threshold again
                fetch('/cart.js')
                  .then(r => r.json())
                  .then(cart => {
                    if (cart.total_price >= this.#threshold2 && cart.items) {
                      const bonusResult = this.#findBonusProductInCart(cart.items);
                      if (!bonusResult?.item) {
                        this.#showBonusProductOffer(cart.items);
                      }
                    }
                  })
                  .catch(err => console.error('[FreeShippingBonus] Error re-adding offer:', err));
              }
            }, 50);
          }
        });

        this.#morphObserver.observe(cartItemsTable, {
          childList: true,
          subtree: true,
        });
      } else if (!cartItemsTable) {
        // Table doesn't exist yet, try again in 100ms
        setTimeout(checkForTable, 100);
      }
    };

    checkForTable();
  }

  /**
   * Handles bonus product variant selector changes
   * @param {Event} event
   */
  #handleBonusVariantChange = async (event) => {
    console.log('[FreeShippingBonus] Change event detected:', {
      target: event.target,
      targetClassList: event.target?.classList?.toString(),
      targetTagName: event.target?.tagName,
    });

    const target = event.target;
    if (!target || !target.classList || !target.classList.contains('cart-items__bonus-variant-select')) {
      console.log('[FreeShippingBonus] Change event ignored - not bonus variant select');
      return;
    }

    console.log('[FreeShippingBonus] Bonus variant select change detected!');

    event.preventDefault();
    event.stopPropagation();
    
    const select = /** @type {HTMLSelectElement} */ (target);
    
    console.log('[FreeShippingBonus] Select element details:', {
      selectedIndex: select.selectedIndex,
      value: select.value,
      lineItem: select.dataset.lineItem,
      productUrl: select.dataset.productUrl,
      productId: select.dataset.productId,
      originalValue: select.dataset.originalValue,
      optionsCount: select.options.length,
    });
    
    const selectedOption = select.options[select.selectedIndex];
    if (!selectedOption) {
      console.error('[FreeShippingBonus] No selected option found');
      return;
    }
    
    const newVariantId = selectedOption.dataset.variantId;
    // data-line-item is already 1-based (item.index + 1 from Liquid template)
    const lineItem = parseInt(select.dataset.lineItem || '0', 10);

    console.log('[FreeShippingBonus] Processing variant change:', {
      newVariantId,
      lineItem,
      selectedOptionValue: selectedOption.value,
      selectedOptionText: selectedOption.text,
      datasetLineItem: select.dataset.lineItem,
    });

    if (!newVariantId || !lineItem) {
      console.error('[FreeShippingBonus] Missing required data:', {
        hasNewVariantId: !!newVariantId,
        hasLineItem: !!lineItem,
        datasetLineItem: select.dataset.lineItem,
        parsedLineItem: lineItem,
      });
      return;
    }

    console.log('[FreeShippingBonus] Starting variant change process...');

    // Disable select element immediately
    select.disabled = true;
    
    // Apply loading state to cart drawer inner
    const cartDrawer = document.querySelector('cart-drawer-component');
    const drawerInner = cartDrawer?.querySelector('.cart-drawer__inner');
    const cartItemsComponent = document.querySelector('cart-items-component');
    
    const applyLoadingState = () => {
      if (drawerInner) {
        drawerInner.classList.add('cart-drawer--loading');
        drawerInner.style.opacity = '0.5';
        drawerInner.style.pointerEvents = 'none';
      }
      if (cartItemsComponent) {
        cartItemsComponent.classList.add('cart-items-disabled');
      }
    };
    
    const removeLoadingState = () => {
      if (drawerInner) {
        drawerInner.classList.remove('cart-drawer--loading');
        drawerInner.style.opacity = '';
        drawerInner.style.pointerEvents = '';
      }
      if (cartItemsComponent) {
        cartItemsComponent.classList.remove('cart-items-disabled');
      }
      select.disabled = false;
    };
    
    applyLoadingState();

    try {
      // First remove the old item, then add new variant
      const removeResponse = await fetch(Theme.routes.cart_change_url, {
        ...fetchConfig('json', {
          body: JSON.stringify({
            line: lineItem,
            quantity: 0,
            sections: document.querySelector('cart-items-component')?.dataset?.sectionId || '',
            sections_url: window.location.pathname,
          }),
        }),
      });

      if (!removeResponse.ok) {
        throw new Error('Failed to remove old variant');
      }

      // Add new variant
      const addFormData = new FormData();
      addFormData.append('id', newVariantId);
      addFormData.append('quantity', '1');
      addFormData.append('properties[_bonus_product]', 'true');

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set();
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });
      if (sectionsToUpdate.size > 0) {
        addFormData.append('sections', Array.from(sectionsToUpdate).join(','));
      }

      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        body: addFormData,
      });

      if (!addResponse.ok) {
        throw new Error('Failed to add new variant');
      }

      const addData = await addResponse.json();

      // Get section ID and morph the section to update UI (like quantity selectors)
      const cartItemsComponent = document.querySelector('cart-items-component');
      const sectionId = cartItemsComponent?.dataset?.sectionId;

      if (sectionId && addData.sections && addData.sections[sectionId]) {
        // Morph the section to update the UI
        morphSection(sectionId, addData.sections[sectionId]);
        
        // Re-apply loading state after morphing (DOM was updated)
        setTimeout(() => {
          applyLoadingState();
          // Update select reference after morphing
          const newSelect = document.querySelector(`#bonus-product-size-${select.id.split('-').pop() || ''}`);
          if (newSelect) {
            newSelect.disabled = true;
          }
        }, 0);
      }

      // Fetch updated cart and dispatch event
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      const newSectionHTML = sectionId && addData.sections?.[sectionId]
        ? new DOMParser().parseFromString(addData.sections[sectionId], 'text/html')
        : null;

      // Grab the new cart item count from the section HTML
      const newCartHiddenItemCount = newSectionHTML?.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : cart.item_count;
      
      document.dispatchEvent(
        new CartUpdateEvent(cart, sectionId || '', {
          source: 'free-shipping-bonus-product',
          itemCount: newCartItemCount,
          sections: addData.sections || {},
        })
      );
    } catch (error) {
      console.error('[FreeShippingBonus] Error changing bonus product variant:', error);
      // Reset select to original value on error
      const currentSelect = document.querySelector(`.cart-items__bonus-variant-select[data-line-item="${lineItem}"]`);
      if (currentSelect) {
        currentSelect.value = select.dataset.originalValue || select.value;
      }
      throw error;
    } finally {
      // Re-enable cart drawer and select
      removeLoadingState();
    }
  };

  /**
   * Handles cart update events
   * @param {Event} event
   */
  #handleCartUpdate = (event) => {
    console.log('[FreeShippingBonus] Cart update event received:', {
      source: event.detail?.data?.source,
      hasResource: !!event.detail?.resource,
      hasItems: !!event.detail?.resource?.items,
      eventType: event.type,
    });

    // Don't ignore events from our own add operations, but skip during processing to prevent loops
    if (event.detail?.data?.source === 'free-shipping-bonus-product' && this.#isProcessing) {
      console.log('[FreeShippingBonus] Ignoring event from own source during processing');
      return;
    }

    // Use cart data from event if available
    const cart = event.detail?.resource;
    if (cart && cart.items) {
      console.log('[FreeShippingBonus] Using cart data from event:', {
        itemCount: cart.items.length,
        totalPrice: cart.total_price,
      });
      
      // Wait a bit for morphing to complete, then re-check
      setTimeout(() => {
        this.#debouncedCheck(cart);
      }, 150);
    } else {
      console.log('[FreeShippingBonus] No cart data in event, fetching from API');
      // Fallback to fetching cart - also wait for morphing
      setTimeout(() => {
        this.#debouncedCheck();
      }, 150);
    }
  };

  /**
   * Checks cart and manages bonus product
   * @param {object | null} cartData
   */
  async #checkAndManageBonusProduct(cartData = null) {
    if (this.#isProcessing) {
      console.log('[FreeShippingBonus] Already processing, skipping check');
      return;
    }

    console.log('[FreeShippingBonus] Starting cart check', {
      hasCartData: !!cartData,
      threshold1: this.#threshold1,
      threshold2: this.#threshold2,
    });

    try {
      this.#isProcessing = true;

      // Fetch cart if not provided
      let cart = cartData;
      if (!cart) {
        console.log('[FreeShippingBonus] Fetching cart from /cart.js');
        const response = await fetch('/cart.js');
        cart = await response.json();
        console.log('[FreeShippingBonus] Cart fetched:', {
          itemCount: cart.items?.length || 0,
          totalPrice: cart.total_price || 0,
        });
      } else {
        console.log('[FreeShippingBonus] Using provided cart data:', {
          itemCount: cart.items?.length || 0,
          totalPrice: cart.total_price || 0,
        });
      }

      if (!cart || !cart.items) {
        console.warn('[FreeShippingBonus] Invalid cart data:', cart);
        return;
      }

      const cartTotal = cart.total_price || 0;
      console.log('[FreeShippingBonus] Cart analysis:', {
        cartTotal,
        threshold2: this.#threshold2,
        meetsThreshold: cartTotal >= this.#threshold2,
        itemCount: cart.items.length,
      });

      const bonusProductResult = this.#findBonusProductInCart(cart.items);
      const bonusProductInCart = bonusProductResult?.item;
      const bonusProductLineIndex = bonusProductResult?.index;
      
      console.log('[FreeShippingBonus] Bonus product check:', {
        bonusProductInCart: !!bonusProductInCart,
        bonusProductKey: bonusProductInCart?.key,
        bonusProductId: bonusProductInCart?.product_id,
        bonusProductLineIndex,
      });

      if (cartTotal >= this.#threshold2) {
        console.log('[FreeShippingBonus] Cart meets tier 2 threshold');
        // Show offer if not in cart, or just ensure it's displayed if in cart
        if (!bonusProductInCart) {
          console.log('[FreeShippingBonus] Bonus product not in cart - showing offer');
          this.#shouldShowOffer = true;
          await this.#showBonusProductOffer(cart.items);
        } else {
          console.log('[FreeShippingBonus] Bonus product already in cart - hiding offer');
          this.#shouldShowOffer = false;
          await this.#hideBonusProductOffer();
        }
      } else {
        console.log('[FreeShippingBonus] Cart does NOT meet tier 2 threshold');
        this.#shouldShowOffer = false;
        // Hide offer and remove from cart if present
        await this.#hideBonusProductOffer();
        
        if (bonusProductInCart && bonusProductLineIndex !== undefined) {
          console.log('[FreeShippingBonus] Bonus product found but threshold not met - removing from cart');
          await this.#removeBonusProduct(cart.items, bonusProductLineIndex);
        } else {
          console.log('[FreeShippingBonus] No bonus product in cart and threshold not met - no action needed');
        }
      }
    } catch (error) {
      console.error('[FreeShippingBonus] Error managing bonus product:', error);
      console.error('[FreeShippingBonus] Error stack:', error.stack);
    } finally {
      this.#isProcessing = false;
      console.log('[FreeShippingBonus] Cart check completed');
    }
  }

  /**
   * Finds bonus product in cart items
   * @param {Array} items
   * @returns {{item: any, index: number} | null}
   */
  #findBonusProductInCart(items) {
    console.log('[FreeShippingBonus] Searching for bonus product in cart:', {
      itemCount: items.length,
      bonusProductId: this.#bonusProductId,
      bonusProductHandle: this.#bonusProductHandle,
    });

    const index = items.findIndex((/** @type {any} */ item) => {
      if (this.#bonusProductId) {
        const matches = item.product_id === this.#bonusProductId;
        if (matches) {
          console.log('[FreeShippingBonus] Found bonus product by ID:', {
            itemKey: item.key,
            productId: item.product_id,
            variantId: item.variant_id,
          });
        }
        return matches;
      }
      // Try to match by handle if available in item data
      const matchesHandle = item.handle === this.#bonusProductHandle || 
             item.product_handle === this.#bonusProductHandle;
      if (matchesHandle) {
        console.log('[FreeShippingBonus] Found bonus product by handle:', {
          itemKey: item.key,
          handle: item.handle || item.product_handle,
        });
      }
      return matchesHandle;
    });

    if (index === -1) {
      console.log('[FreeShippingBonus] Bonus product not found in cart items');
      items.forEach((/** @type {any} */ item, idx) => {
        console.log(`[FreeShippingBonus] Cart item ${idx}:`, {
          productId: item.product_id,
          handle: item.handle || item.product_handle,
          variantId: item.variant_id,
        });
      });
      return null;
    }

    return { item: items[index], index };
  }

  /**
   * Gets the most common size from cart items
   * @param {Array} cartItems
   */
  #getMostCommonSize(cartItems) {
    console.log('[FreeShippingBonus] Finding most common size from cart items:', {
      itemCount: cartItems.length,
    });

    const sizeCounts = new Map();

    cartItems.forEach((/** @type {any} */ item, index) => {
      if (!item) return;

      // Check properties first (if size is stored as a property)
      if (item.properties) {
        const sizeProperty = Object.entries(item.properties).find(([key]) => {
          const keyLower = key.toLowerCase();
          return keyLower.includes('size') && !keyLower.startsWith('_');
        });

        if (sizeProperty) {
          const size = sizeProperty[1];
          console.log(`[FreeShippingBonus] Item ${index} size from properties:`, size);
          sizeCounts.set(size, (sizeCounts.get(size) || 0) + (item.quantity || 1));
          return; // Found size, skip other checks
        }
      }

      // Check options array (first option is usually Size in cart.js)
      if (item.options && Array.isArray(item.options) && item.options.length > 0) {
        const firstOption = item.options[0];
        console.log(`[FreeShippingBonus] Item ${index} first option:`, firstOption);
        if (firstOption) {
          sizeCounts.set(firstOption, (sizeCounts.get(firstOption) || 0) + (item.quantity || 1));
        }
      } else {
        console.log(`[FreeShippingBonus] Item ${index} has no options array`);
      }

      // Also check variant_options if available
      if (item.variant_options && Array.isArray(item.variant_options) && item.variant_options.length > 0) {
        const firstVariantOption = item.variant_options[0];
        console.log(`[FreeShippingBonus] Item ${index} first variant_option:`, firstVariantOption);
        if (firstVariantOption) {
          sizeCounts.set(firstVariantOption, (sizeCounts.get(firstVariantOption) || 0) + (item.quantity || 1));
        }
      } else {
        console.log(`[FreeShippingBonus] Item ${index} has no variant_options array`);
      }
    });

    console.log('[FreeShippingBonus] Size counts:', Array.from(sizeCounts.entries()));

    // Return most common size
    if (sizeCounts.size === 0) {
      console.log('[FreeShippingBonus] No sizes found in cart items - returning null');
      return null;
    }

    let mostCommonSize = null;
    let maxCount = 0;

    sizeCounts.forEach((count, size) => {
      if (count > maxCount) {
        maxCount = count;
        mostCommonSize = size;
      }
    });

    console.log('[FreeShippingBonus] Most common size determined:', {
      size: mostCommonSize,
      count: maxCount,
    });

    return mostCommonSize;
  }

  /**
   * Fetches product data
   */
  async #fetchProduct() {
    let productUrl = `/products/${this.#bonusProductHandle}`;
    
    console.log('[FreeShippingBonus] Fetching product:', {
      handle: this.#bonusProductHandle,
      productId: this.#bonusProductId,
      url: productUrl,
    });

    if (this.#bonusProductId) {
      // Try to get handle from product ID if we have it
      try {
        const testUrl = `/products/${this.#bonusProductHandle}.js`;
        console.log('[FreeShippingBonus] Testing product URL:', testUrl);
        const testResponse = await fetch(testUrl);
        if (testResponse.ok) {
          const product = await testResponse.json();
          console.log('[FreeShippingBonus] Product fetched successfully:', {
            id: product.id,
            title: product.title,
            variantCount: product.variants?.length || 0,
          });
          return product;
        }
      } catch (e) {
        console.warn('[FreeShippingBonus] Error testing product URL:', e);
        // Fallback to using ID
      }
    }

    console.log('[FreeShippingBonus] Fetching product from URL:', `${productUrl}.js`);
    const response = await fetch(`${productUrl}.js`);
    if (!response.ok) {
      console.error('[FreeShippingBonus] Failed to fetch product:', {
        status: response.status,
        statusText: response.statusText,
        url: productUrl,
      });
      throw new Error(`Failed to fetch product: ${response.status}`);
    }

    const product = await response.json();
    console.log('[FreeShippingBonus] Product fetched:', {
      id: product.id,
      title: product.title,
      variantCount: product.variants?.length || 0,
      variants: product.variants?.map((/** @type {any} */ v) => ({
        id: v.id,
        title: v.title,
        available: v.available,
        options: v.options,
      })),
    });

    return product;
  }

  /**
   * Finds variant matching the size
   * @param {any} product
   * @param {string | null} size
   */
  #findVariantBySize(product, size) {
    console.log('[FreeShippingBonus] Finding variant by size:', {
      size,
      variantCount: product.variants?.length || 0,
    });

    if (!size || !product.variants) {
      console.log('[FreeShippingBonus] No size provided or no variants - using default');
      const defaultVariant = product.selected_or_first_available_variant || product.variants?.[0];
      console.log('[FreeShippingBonus] Default variant:', {
        id: defaultVariant?.id,
        title: defaultVariant?.title,
        available: defaultVariant?.available,
      });
      return defaultVariant;
    }

    // Find variant with matching size option
    const sizeLower = size.toLowerCase().trim();
    console.log('[FreeShippingBonus] Searching for size:', sizeLower);
    
    // Try to find which option position is "Size"
    let sizeOptionIndex = -1;
    if (product.options) {
      sizeOptionIndex = product.options.findIndex((/** @type {any} */ opt) => {
        const optName = typeof opt === 'string' ? opt : (opt?.name || '');
        return optName.toLowerCase() === 'size';
      });
    }

    const matchingVariant = product.variants.find((/** @type {any} */ variant) => {
      // Try option1, option2, etc. format first
      if (sizeOptionIndex !== -1) {
        const optionKey = `option${sizeOptionIndex + 1}`;
        const variantSize = variant[optionKey];
        if (variantSize && variantSize.toLowerCase().trim() === sizeLower) {
          console.log('[FreeShippingBonus] Found matching variant by option key:', {
            id: variant.id,
            title: variant.title,
            optionKey,
            size: variantSize,
            available: variant.available,
          });
          return true;
        }
      }
      
      // Fallback to options array format
      if (variant.options && Array.isArray(variant.options)) {
        const matches = variant.options.some((/** @type {any} */ option) => {
          const optionLower = option?.toLowerCase().trim();
          return optionLower === sizeLower;
        });

        if (matches) {
          console.log('[FreeShippingBonus] Found matching variant by options array:', {
            id: variant.id,
            title: variant.title,
            options: variant.options,
            available: variant.available,
          });
        }

        return matches;
      }
      
      return false;
    });

    const selectedVariant = matchingVariant || product.selected_or_first_available_variant || product.variants?.[0];
    console.log('[FreeShippingBonus] Selected variant:', {
      id: selectedVariant?.id,
      title: selectedVariant?.title,
      options: selectedVariant?.options,
      available: selectedVariant?.available,
      wasMatched: !!matchingVariant,
    });

    return selectedVariant;
  }

  /**
   * Adds bonus product to cart
   * @param {Array} cartItems
   */
  async #addBonusProduct(cartItems) {
    console.log('[FreeShippingBonus] Starting to add bonus product to cart');
    
    try {
      const product = await this.#fetchProduct();
      if (!product) {
        console.error('[FreeShippingBonus] Product fetch returned null/undefined');
        throw new Error('Product not found');
      }

      // Get most common size from cart
      const mostCommonSize = this.#getMostCommonSize(cartItems);
      console.log('[FreeShippingBonus] Most common size from cart:', mostCommonSize);
      
      const variant = this.#findVariantBySize(product, mostCommonSize);

      if (!variant) {
        console.error('[FreeShippingBonus] No variant found/selected');
        return;
      }

      if (!variant.available) {
        console.warn('[FreeShippingBonus] Bonus product variant not available:', {
          variantId: variant.id,
          variantTitle: variant.title,
        });
        return;
      }

      console.log('[FreeShippingBonus] Preparing to add variant to cart:', {
        variantId: variant.id,
        variantTitle: variant.title,
        available: variant.available,
      });

      // Add to cart
      const formData = new FormData();
      formData.append('id', variant.id.toString());
      formData.append('quantity', '1');
      formData.append('properties[_bonus_product]', 'true');

      console.log('[FreeShippingBonus] FormData prepared:', {
        variantId: variant.id.toString(),
        quantity: '1',
        hasBonusProperty: true,
      });

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set();
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });
      if (sectionsToUpdate.size > 0) {
        formData.append('sections', Array.from(sectionsToUpdate).join(','));
        console.log('[FreeShippingBonus] Sections to update:', Array.from(sectionsToUpdate));
      } else {
        console.warn('[FreeShippingBonus] No cart-items-component found for sections update');
      }

      console.log('[FreeShippingBonus] Sending add to cart request:', {
        url: Theme.routes.cart_add_url,
        variantId: variant.id.toString(),
      });

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        body: formData,
      });

      console.log('[FreeShippingBonus] Add to cart response:', {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[FreeShippingBonus] Failed to add product:', errorData);
        throw new Error(errorData.description || 'Failed to add bonus product');
      }

      const retrievalData = await response.json();
      console.log('[FreeShippingBonus] Add to cart success:', {
        hasSections: !!retrievalData.sections,
        sectionKeys: retrievalData.sections ? Object.keys(retrievalData.sections) : [],
      });

      // Fetch updated cart
      console.log('[FreeShippingBonus] Fetching updated cart');
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      console.log('[FreeShippingBonus] Updated cart:', {
        itemCount: cart.item_count,
        totalPrice: cart.total_price,
      });

      const sectionId = document.querySelector('cart-items-component')?.dataset?.sectionId;
      console.log('[FreeShippingBonus] Dispatching CartAddEvent:', {
        sectionId,
        variantId: variant.id.toString(),
        productId: product.id.toString(),
      });
      
      document.dispatchEvent(
        new CartAddEvent(cart, variant.id.toString(), {
          source: 'free-shipping-bonus-product',
          itemCount: 1,
          productId: product.id.toString(),
          sections: retrievalData.sections || {},
        })
      );

      console.log('[FreeShippingBonus] Bonus product added successfully');
    } catch (error) {
      console.error('[FreeShippingBonus] Error adding bonus product:', error);
      console.error('[FreeShippingBonus] Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name,
      });
    }
  }

  /**
   * Removes bonus product from cart
   * @param {Array} cartItems - The cart items array (to find the current line number)
   * @param {number} itemIndex - The 0-based index of the bonus item in the cart items array
   */
  async #removeBonusProduct(cartItems, itemIndex) {
    console.log('[FreeShippingBonus] Starting bonus product removal:', {
      itemIndex,
      cartItemCount: cartItems.length,
    });

    try {
      // Line numbers are 1-based in Shopify Cart API
      const lineNumber = itemIndex + 1;
      
      console.log('[FreeShippingBonus] Removing item at line number:', lineNumber);

      const cartItemsComponent = document.querySelector('cart-items-component');
      const sectionId = cartItemsComponent?.dataset?.sectionId || '';
      
      const response = await fetch(Theme.routes.cart_change_url, {
        ...fetchConfig('json', {
          body: JSON.stringify({
            line: lineNumber,
            quantity: 0,
            sections: sectionId,
            sections_url: window.location.pathname,
          }),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to remove bonus product');
      }

      const parsedResponse = await response.text();
      const parsedJson = JSON.parse(parsedResponse);

      console.log('[FreeShippingBonus] Bonus product removal response:', {
        hasSections: !!parsedJson.sections,
        sectionKeys: parsedJson.sections ? Object.keys(parsedJson.sections) : [],
      });

      // Morph the section to update UI (like quantity selectors)
      if (sectionId && parsedJson.sections && parsedJson.sections[sectionId]) {
        console.log('[FreeShippingBonus] Morphing section to update UI');
        morphSection(sectionId, parsedJson.sections[sectionId]);
      }

      // Fetch updated cart
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      console.log('[FreeShippingBonus] Updated cart after removal:', {
        itemCount: cart.item_count,
        totalPrice: cart.total_price,
      });

      // Dispatch event with updated cart data
      document.dispatchEvent(
        new CartUpdateEvent(cart, sectionId, {
          source: 'free-shipping-bonus-product',
          itemCount: cart.item_count,
          sections: parsedJson.sections || {},
        })
      );

      console.log('[FreeShippingBonus] Bonus product removed successfully');
    } catch (error) {
      console.error('[FreeShippingBonus] Error removing bonus product:', error);
      console.error('[FreeShippingBonus] Error details:', {
        message: error.message,
        stack: error.stack,
      });
    }
  }

  /**
   * Shows the bonus product offer in the cart drawer
   * @param {Array} cartItems
   */
  async #showBonusProductOffer(cartItems) {
    console.log('[FreeShippingBonus] Showing bonus product offer');
    
    // Fetch product if not cached
    if (!this.#cachedProduct) {
      this.#cachedProduct = await this.#fetchProduct();
    }

    if (!this.#cachedProduct) {
      console.error('[FreeShippingBonus] Cannot show offer - product not found');
      return;
    }

    // Get most common size from cart for default selection
    const mostCommonSize = this.#getMostCommonSize(cartItems);
    const defaultVariant = this.#findVariantBySize(this.#cachedProduct, mostCommonSize);

    // Wait for cart items table to be available (with retry)
    const maxRetries = 10;
    let retries = 0;
    let cartItemsTable = null;

    while (retries < maxRetries && !cartItemsTable) {
      cartItemsTable = document.querySelector('.cart-items__table tbody');
      if (!cartItemsTable) {
        console.log(`[FreeShippingBonus] Cart items table not found, retrying (${retries + 1}/${maxRetries})...`);
        await new Promise(resolve => setTimeout(resolve, 100));
        retries++;
      }
    }

    if (!cartItemsTable) {
      console.error('[FreeShippingBonus] Cannot find cart items table tbody after retries');
      return;
    }

    // Find or create the offer row in the cart items table
    let offerRow = document.querySelector('.cart-items__bonus-offer-row');
    if (!offerRow) {
      // Create the offer row
      offerRow = document.createElement('tr');
      offerRow.className = 'cart-items__table-row cart-items__bonus-offer-row';
      offerRow.setAttribute('role', 'row');
      offerRow.setAttribute('data-bonus-product-offer', 'true');
      
      // Insert at the end of the tbody
      cartItemsTable.appendChild(offerRow);
    }

    // Render the offer row HTML
    this.#renderBonusProductOfferRow(offerRow, this.#cachedProduct, defaultVariant);
    
    // Update button when variant selector changes
    const variantSelect = offerRow.querySelector('.bonus-product-offer__variant-select');
    if (variantSelect) {
      // Remove old listener if exists
      if (this.#offerVariantChangeHandler) {
        variantSelect.removeEventListener('change', this.#offerVariantChangeHandler);
      }
      // Create new handler
      this.#offerVariantChangeHandler = (e) => {
        const select = /** @type {HTMLSelectElement} */ (e.target);
        const selectedOption = select.options[select.selectedIndex];
        const variantId = selectedOption.dataset.variantId;
        const addButton = offerRow.querySelector('.bonus-product-offer__add-button');
        if (addButton && variantId) {
          addButton.dataset.variantId = variantId;
        }
      };
      variantSelect.addEventListener('change', this.#offerVariantChangeHandler);
    }
  }

  /**
   * Hides the bonus product offer
   */
  async #hideBonusProductOffer() {
    console.log('[FreeShippingBonus] Hiding bonus product offer');
    const offerRow = document.querySelector('.cart-items__bonus-offer-row');
    if (offerRow) {
      offerRow.remove();
    }
    this.#shouldShowOffer = false;
  }

  /**
   * Renders the bonus product offer as a table row in cart items
   * @param {HTMLElement} row
   * @param {any} product
   * @param {any} defaultVariant
   */
  #renderBonusProductOfferRow(row, product, defaultVariant) {
    // Find size option - product JSON has 'options' array of strings, and variants have 'option1', 'option2', etc.
    let sizeOption = null;
    let sizeOptionIndex = -1;
    
    if (product.options) {
      const sizeIndex = product.options.findIndex((/** @type {any} */ opt) => {
        const optName = typeof opt === 'string' ? opt : (opt?.name || '');
        return optName.toLowerCase() === 'size';
      });
      if (sizeIndex !== -1) {
        sizeOptionIndex = sizeIndex;
        // Get unique values for this option from variants
        const sizeValues = new Set();
        product.variants?.forEach((/** @type {any} */ v) => {
          const optionKey = `option${sizeIndex + 1}`;
          if (v[optionKey]) {
            sizeValues.add(v[optionKey]);
          }
        });
        sizeOption = {
          name: 'Size',
          values: Array.from(sizeValues),
        };
      }
    }

    const selectedVariantId = defaultVariant?.id || product.selected_or_first_available_variant?.id || product.variants?.[0]?.id;

    // Get product image URL - handle different formats
    let imageUrl = '';
    if (product.images && product.images.length > 0) {
      // Use last image from images array (matching make-it-a-set pattern)
      imageUrl = product.images[product.images.length - 1];
    } else if (product.featured_image) {
      imageUrl = typeof product.featured_image === 'string' 
        ? product.featured_image 
        : (product.featured_image.src || product.featured_image);
    }

    // Build the table row HTML matching cart item structure
    const imageHtml = imageUrl 
      ? `<a href="/products/${product.handle}" class="cart-items__media-container" style="--ratio: 0.6667;">
          <img src="${imageUrl}?width=250" alt="${product.title}" class="cart-items__media-image border-style" loading="lazy" />
        </a>`
      : '<div class="cart-items__media-container" style="--ratio: 0.6667;"></div>';

    const sizeSelectHtml = sizeOption 
      ? `<div class="cart-items__bonus-variant-selector" style="margin-top: 0.5rem;">
          <label for="bonus-offer-size-select" class="visually-hidden">Select Size</label>
          <select id="bonus-offer-size-select" class="bonus-product-offer__variant-select" data-product-id="${product.id}" style="width: 100%; max-width: 124px; padding: var(--padding-xs) var(--padding-sm); border: var(--style-border-width) solid var(--color-border); border-radius: var(--style-border-radius); background-color: var(--color-background); font-size: var(--font-size--sm);">
            ${sizeOption.values.map((/** @type {any} */ value) => {
              const optionKey = `option${sizeOptionIndex + 1}`;
              const variant = product.variants?.find((/** @type {any} */ v) => {
                const variantOptionValue = v[optionKey];
                return variantOptionValue && variantOptionValue.toLowerCase() === value.toLowerCase();
              });
              const variantId = variant?.id || '';
              const isSelected = variantId && variantId.toString() === selectedVariantId?.toString();
              const isAvailable = variant?.available !== false;
              return `<option value="${value}" data-variant-id="${variantId}" ${isSelected ? 'selected' : ''} ${!isAvailable ? 'disabled' : ''}>${value}${!isAvailable ? ' (Unavailable)' : ''}</option>`;
            }).join('')}
          </select>
        </div>`
      : '';

    row.innerHTML = `
      <td class="cart-items__media" role="cell" headers="productImage">
        ${imageHtml}
      </td>
      <td class="cart-items__details cart-primary-typography" role="cell" headers="productInformation">
        <p>
          <span class="cart-items__badge" style="font-size: var(--font-size--xs); font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; display: block; margin-bottom: 0.25rem;">FREE GIFT</span>
          <div class="cart-items__title">${product.title}</div>
        </p>
        ${sizeSelectHtml}
      </td>
      <td class="cart-items__quantity" role="cell" headers="quantity">
        <button type="button" class="button bonus-product-offer__add-button" data-variant-id="${selectedVariantId}" style="width: 100%; max-width: 124px;">
          Add to Cart
        </button>
      </td>
      <td class="cart-items__price cart-secondary-typography" role="cell" headers="productTotal">
        <text-component value="free">free</text-component>
      </td>
    `;
  }

  /**
   * Handles the add button click for bonus product offer
   * @param {Event} event
   */
  #handleBonusAddButtonClick = async (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    
    if (!target.classList?.contains('bonus-product-offer__add-button')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    console.log('[FreeShippingBonus] Add button clicked');

    // Get selected variant from the select dropdown
    const offerRow = target.closest('.cart-items__bonus-offer-row');
    const variantSelect = offerRow?.querySelector('.bonus-product-offer__variant-select');
    
    let variantId = target.dataset.variantId;
    if (variantSelect && variantSelect instanceof HTMLSelectElement) {
      const selectedOption = variantSelect.options[variantSelect.selectedIndex];
      if (selectedOption) {
        variantId = selectedOption.dataset.variantId || variantId;
      }
    }

    if (!variantId) {
      console.error('[FreeShippingBonus] No variant ID found');
      return;
    }

    console.log('[FreeShippingBonus] Adding bonus product to cart with variant:', variantId);

    // Disable button and show loading state
    if (target instanceof HTMLButtonElement) {
      target.disabled = true;
      target.textContent = 'Adding...';
    }

    try {
      // Fetch cart items to determine size
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Add to cart using existing method
      await this.#addBonusProductWithVariant(cart.items, variantId);
      
      // Hide offer row after successful add
      await this.#hideBonusProductOffer();
    } catch (error) {
      console.error('[FreeShippingBonus] Error adding bonus product:', error);
      if (target instanceof HTMLButtonElement) {
        target.disabled = false;
        target.textContent = 'Add to Cart';
      }
      alert('Failed to add bonus product. Please try again.');
    }
  };

  /**
   * Adds bonus product to cart with specific variant
   * @param {Array} cartItems
   * @param {string | number} variantId
   */
  async #addBonusProductWithVariant(cartItems, variantId) {
    console.log('[FreeShippingBonus] Adding bonus product with variant:', variantId);

    const formData = new FormData();
    formData.append('id', variantId.toString());
    formData.append('quantity', '1');
    formData.append('properties[_bonus_product]', 'true');

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set();
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    if (sectionsToUpdate.size > 0) {
      formData.append('sections', Array.from(sectionsToUpdate).join(','));
    }

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.description || 'Failed to add bonus product');
    }

    const retrievalData = await response.json();

    // Morph the section to update UI
    const cartItemsComponent = document.querySelector('cart-items-component');
    const sectionId = cartItemsComponent?.dataset?.sectionId;
    if (sectionId && retrievalData.sections && retrievalData.sections[sectionId]) {
      morphSection(sectionId, retrievalData.sections[sectionId]);
    }

    // Fetch updated cart and dispatch event
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();

    document.dispatchEvent(
      new CartAddEvent(cart, variantId.toString(), {
        source: 'free-shipping-bonus-product',
        itemCount: cart.item_count,
        productId: this.#bonusProductId?.toString() || '',
        sections: retrievalData.sections || {},
      })
    );
  }
}

if (!customElements.get('free-shipping-bonus-product')) {
  customElements.define('free-shipping-bonus-product', FreeShippingBonusProduct);
}

